/**
 * Daily Visit Report PDF builder.
 *
 * Composes a single-page A4 PDF containing:
 *   • Title block — sales rep name + date range
 *   • Summary chips — total km, pings, stops, distinct hours active
 *   • Snapshot of the live Leaflet map (via html2canvas)
 *   • Table of stops with start/end timestamps + duration
 *
 * Why client-side?
 *   • No third-party API key / paid map provider needed.
 *   • Uses the map the user already has on screen — what they SEE is what
 *     the customer/manager gets.
 *   • jsPDF + jspdf-autotable + html2canvas are MIT-licensed and tiny.
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

const PAGE_MARGIN_MM = 12;

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

/** Distinct hours-of-day that contain at least one ping. */
const activeHours = (points) => {
  const set = new Set();
  (points || []).forEach((p) => {
    if (!p.timestamp) return;
    try {
      set.add(new Date(p.timestamp).getHours());
    } catch {
      /* ignore */
    }
  });
  return set.size;
};

/**
 * @param {object} opts
 * @param {string} opts.userName          — Rep name for the title
 * @param {string} opts.fromDate          — "YYYY-MM-DD"
 * @param {string} opts.toDate            — "YYYY-MM-DD" (same as from for 1-day)
 * @param {Array}  opts.points            — Trail pings [{lat,lng,timestamp}]
 * @param {Array}  opts.stops             — Stop objects from detectStops()
 * @param {number} opts.totalKm           — Distance in km
 * @param {HTMLElement} opts.mapEl        — The .leaflet-container DOM node
 * @param {string} opts.companyName       — Letterhead label
 * @returns {Promise<void>}               — Triggers a save() side-effect
 */
export const buildVisitReportPdf = async ({
  userName,
  fromDate,
  toDate,
  points = [],
  stops = [],
  totalKm = 0,
  mapEl,
  companyName = '',
}) => {
  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let cursorY = PAGE_MARGIN_MM;

  // ── Header ──────────────────────────────────────────────────────────
  if (companyName) {
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text(companyName, PAGE_MARGIN_MM, cursorY);
    cursorY += 4;
  }
  doc.setFontSize(16);
  doc.setTextColor(20);
  doc.text('Daily Visit Report', PAGE_MARGIN_MM, cursorY + 2);
  cursorY += 9;

  doc.setFontSize(11);
  doc.setTextColor(40);
  doc.text(`Rep: ${userName || '—'}`, PAGE_MARGIN_MM, cursorY);
  cursorY += 5;
  const dateLabel = toDate && toDate !== fromDate
    ? `${fromDate} → ${toDate}`
    : fromDate;
  doc.text(`Date: ${dateLabel}`, PAGE_MARGIN_MM, cursorY);
  cursorY += 6;

  // ── Summary strip ───────────────────────────────────────────────────
  const summary = [
    ['Distance', `${Number(totalKm || 0).toFixed(2)} km`],
    ['Pings', String(points.length)],
    ['Stops (≥5 min)', String(stops.length)],
    ['Active hours', String(activeHours(points))],
  ];
  doc.setFontSize(9);
  doc.setTextColor(80);
  const colW = (pageWidth - 2 * PAGE_MARGIN_MM) / summary.length;
  summary.forEach(([label, value], i) => {
    const x = PAGE_MARGIN_MM + i * colW;
    doc.setDrawColor(220);
    doc.setFillColor(245, 249, 255);
    doc.roundedRect(x, cursorY, colW - 2, 14, 1.5, 1.5, 'FD');
    doc.setTextColor(95);
    doc.text(label, x + 3, cursorY + 5);
    doc.setFontSize(12);
    doc.setTextColor(15);
    doc.text(value, x + 3, cursorY + 11);
    doc.setFontSize(9);
  });
  cursorY += 18;

  // ── Embedded map snapshot ────────────────────────────────────────────
  if (mapEl) {
    try {
      const canvas = await html2canvas(mapEl, {
        useCORS: true,            // OSM tiles set CORS headers, but be defensive
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        scale: 1.4,
      });
      const imgData = canvas.toDataURL('image/jpeg', 0.78);
      const usableWidth = pageWidth - 2 * PAGE_MARGIN_MM;
      const aspect = canvas.height / canvas.width;
      const imgH = Math.min(110, usableWidth * aspect);
      doc.addImage(imgData, 'JPEG', PAGE_MARGIN_MM, cursorY, usableWidth, imgH);
      cursorY += imgH + 4;
    } catch (err) {
      // OSM tiles occasionally taint the canvas if a tile failed to set CORS.
      // Don't break the PDF — just skip the snapshot.
      doc.setTextColor(170, 30, 30);
      doc.setFontSize(8);
      doc.text('Map snapshot unavailable (tile CORS).', PAGE_MARGIN_MM, cursorY + 4);
      cursorY += 8;
    }
  }

  // ── Stops table ─────────────────────────────────────────────────────
  if (stops.length) {
    autoTable(doc, {
      startY: cursorY,
      head: [['#', 'Started', 'Ended', 'Duration', 'Lat, Lng']],
      body: stops.map((s, i) => [
        i + 1,
        fmtDateTime(s.startedAt),
        fmtDateTime(s.endedAt),
        `${s.durationMinutes} min`,
        `${Number(s.lat).toFixed(5)}, ${Number(s.lng).toFixed(5)}`,
      ]),
      styles: { fontSize: 8, cellPadding: 1.5 },
      headStyles: { fillColor: [37, 99, 235], textColor: 255 },
      margin: { left: PAGE_MARGIN_MM, right: PAGE_MARGIN_MM },
      theme: 'striped',
    });
  } else {
    doc.setFontSize(9);
    doc.setTextColor(110);
    doc.text('No stops longer than 5 minutes detected in this period.', PAGE_MARGIN_MM, cursorY + 4);
  }

  // ── Footer ──────────────────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  doc.setFontSize(7);
  doc.setTextColor(150);
  doc.text(
    `Generated ${new Date().toLocaleString()} · OpenStreetMap · ${totalPages} page${totalPages === 1 ? '' : 's'}`,
    PAGE_MARGIN_MM,
    doc.internal.pageSize.getHeight() - 6
  );

  // ── Save ────────────────────────────────────────────────────────────
  const safeName = (userName || 'rep').replace(/[^\w-]+/g, '_');
  const safeDates = toDate && toDate !== fromDate ? `${fromDate}_to_${toDate}` : fromDate;
  doc.save(`visit_report_${safeName}_${safeDates}.pdf`);
};
